"use client";

import { useTranslations, useLocale } from "use-intl";

import { ChevronRightIcon, GlobeIcon, ReleaseIcon } from "@/components/appIcons";
import { GradeBadge } from "@/components/collection/grade-badge";
import { CountryFlag } from "@/components/country-flag";
import { Link } from "@/components/link";
import { SellerBadge } from "@/components/marketplace/seller-badge";
import { Price } from "@/components/Price";
import { formatCountry } from "@garimpa/lib/countries";

interface ListingCardProps {
  id: string;
  salePrice: string;
  saleCurrency: string;
  recordGrade?: string | null;
  sleeveGrade?: string | null;
  saleNotes?: string | null;
  salePhotos?: string[] | null;
  sellerName: string | null;
  sellerUsername?: string | null;
  sellerAvatarUrl?: string | null;
  sellerCountry?: string | null;
  saleCreatedAt?: string | null;
  analyticsSurface?: string;
}

export function ListingCard({
  id,
  salePrice,
  saleCurrency,
  recordGrade,
  sleeveGrade,
  saleNotes,
  salePhotos,
  sellerName,
  sellerUsername,
  sellerAvatarUrl,
  sellerCountry,
}: ListingCardProps) {
  const t = useTranslations("shared.marketplace");
  const locale = useLocale();

  const hasPhoto = salePhotos && salePhotos.length > 0;
  const countryName = sellerCountry ? formatCountry(sellerCountry, locale) : null;
  const truncatedNotes =
    saleNotes && saleNotes.length > 80 ? `${saleNotes.slice(0, 80)}...` : saleNotes;

  return (
    <Link
      to={`/listings/${id}`}
      className="group flex items-center gap-4 p-4 rounded-lg bg-secondary/50 border border-border hover:border-primary hover:bg-secondary transition-all"
    >
      {/* Thumbnail */}
      <div className="shrink-0 w-16 h-16 rounded-md overflow-hidden bg-muted flex items-center justify-center">
        {hasPhoto ? (
          <img
            src={salePhotos[0]}
            alt={t("viewListing")}
            width={64}
            height={64}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <ReleaseIcon className="text-2xl text-muted-foreground" />
        )}
      </div>
      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-base group-hover:text-primary">
            <Price amount={salePrice} currency={saleCurrency} />
          </span>
          <GradeBadge recordGrade={recordGrade} sleeveGrade={sleeveGrade} />
        </div>
        <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
          <SellerBadge
            username={sellerUsername}
            avatarUrl={sellerAvatarUrl}
            name={sellerName}
            disableLink
          />
          {sellerCountry && (
            <span className="flex items-center gap-1" title={countryName ?? sellerCountry}>
              <CountryFlag
                code={sellerCountry}
                decorative
                size="sm"
                fallback={<GlobeIcon className="text-xs" />}
              />
            </span>
          )}
        </div>
        {truncatedNotes && (
          <p className="text-xs text-muted-foreground/70 mt-1 truncate">{truncatedNotes}</p>
        )}
      </div>
      {/* Chevron */}
      <ChevronRightIcon className="text-muted-foreground group-hover:text-primary shrink-0" />
    </Link>
  );
}
